import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import ProposalProductLineCard from "@/components/proposal/ProposalProductLineCard";
import DtoolsReviewLineDeleteButton from "@/components/sales/dtools/DtoolsReviewLineDeleteButton";
import { useDtoolsPdfPackageMode } from "@/components/sales/dtools/DtoolsPdfPackageContext";
import { useDtoolsReviewLineActions } from "@/components/sales/dtools/DtoolsReviewLineActionsContext";
import { useDtoolsProposalDisplay } from "@/components/sales/dtools/DtoolsProposalDisplayContext";
import {
  dtoolsStagingLineToProductCardProps,
} from "@/lib/dtoolsProposalLineAdapter";
import { shouldEnableProposalProductImageLightbox } from "@/lib/proposalProductImageLightbox";
import type { DtoolsStagingLine } from "@shared/dtoolsJsonStaging";
import type { DtoolsReviewPrimary } from "@shared/dtoolsStagingTransform";
import { voViewHref } from "@shared/variationOrderPresentation";
import { ChevronDown } from "lucide-react";
import { formatDtoolsMoney } from "@/lib/dtoolsJsonFormat";
import { isHardwareMarkupEligible, resolveLineBaseUnitPrice } from "@shared/proposalHardwareMarkup";

type Props = {
  line: DtoolsStagingLine;
  indent?: boolean;
  compact?: boolean;
  hideImage?: boolean;
};

function VoViewLink({ line }: { line: DtoolsStagingLine }) {
  const display = useDtoolsProposalDisplay();
  const voAnn = line.variationOrderAnnotation;
  if (!voAnn) return null;
  const href = voViewHref({
    staffLinks: display.audience === "staff",
    voId: voAnn.voId,
    publicToken: voAnn.publicToken,
  });
  if (!href) return null;
  return (
    <p className={cn("text-xs", line.parentId || line.isAccessory ? "ml-5" : "ml-1")}>
      <a href={href} className="font-medium text-[#1A2744] underline-offset-2 hover:underline">
        View Variation Order
      </a>
    </p>
  );
}

function DtoolsReviewLineCardShell({
  line,
  indent,
  compact,
  hideImage = false,
}: Props) {
  const display = useDtoolsProposalDisplay();
  const lineActions = useDtoolsReviewLineActions();
  const pdfPackageMode = useDtoolsPdfPackageMode();
  const isStaff = display.audience === "staff";
  const showDelete = isStaff && lineActions != null && line.variationOrderAnnotation?.kind !== "removed";
  const baseUnitPrice = resolveLineBaseUnitPrice(line);
  const baseLineTotal = Math.round(baseUnitPrice * Math.max(1, Number(line.quantity) || 1) * 100) / 100;
  const showBasePrice =
    isStaff &&
    display.showInternalMarkupBasePrices === true &&
    isHardwareMarkupEligible(line) &&
    Math.abs(baseLineTotal - Number(line.lineTotal ?? 0)) >= 0.005;

  return (
    <div className={cn("space-y-1", indent && "ml-4")}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <ProposalProductLineCard
            {...dtoolsStagingLineToProductCardProps(line, {
              indent: false,
              compact,
              hidePrice: display.hideItemPricing,
              hideImage,
              compactCopy: pdfPackageMode,
              pdfPremiumLayout: pdfPackageMode && !indent && !compact,
              showCleoDescriptionPending: isStaff,
              cleoDescriptionPendingMessage: display.cleoDescriptionPendingMessage,
              enableImageLightbox: shouldEnableProposalProductImageLightbox(display.audience),
            })}
            internalBasePriceFormatted={showBasePrice ? formatDtoolsMoney(baseLineTotal) : null}
          />
        </div>
        {showDelete ? (
          <DtoolsReviewLineDeleteButton
            line={line}
            lines={lineActions.lines}
            onLinesChange={lineActions.onLinesChange}
            compact={compact}
          />
        ) : null}
      </div>
      <VoViewLink line={line} />
    </div>
  );
}

/** Thin wrapper — presentation lives in ProposalProductLineCard. */
export default function DtoolsReviewLineCard(props: Props) {
  return <DtoolsReviewLineCardShell {...props} />;
}

export function DtoolsReviewPrimaryBlock({ block }: { block: DtoolsReviewPrimary }) {
  const pdfPackageMode = useDtoolsPdfPackageMode();
  const hasVoInSupporting = block.accessories.some((acc) => Boolean(acc.variationOrderAnnotation));
  const [supportingExpanded, setSupportingExpanded] = useState(hasVoInSupporting);
  const supportingCount = block.accessories.length;
  const showSupporting = supportingCount > 0 && !pdfPackageMode;
  const expanded = pdfPackageMode || supportingExpanded || hasVoInSupporting;

  return (
    <div className="space-y-2">
      <DtoolsReviewLineCardShell line={block.line} />
      {showSupporting ? (
        <div className="space-y-2">
          {!pdfPackageMode ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                "h-8 gap-1.5 px-2 text-xs font-medium text-[#1A2744]/75 hover:bg-[#1A2744]/5",
                "ml-1 border-l-2 border-[#C8A96A]/50 pl-3",
              )}
              aria-expanded={expanded}
              onClick={() => setSupportingExpanded((open) => !open)}
            >
              <ChevronDown
                className={cn("h-3.5 w-3.5 shrink-0 transition-transform", expanded && "rotate-180")}
                aria-hidden
              />
              {expanded
                ? "Hide Supporting Components"
                : `Supporting Components (${supportingCount})`}
            </Button>
          ) : (
            <p className="ml-1 border-l-2 border-[#C8A96A]/50 pl-3 text-[11px] font-medium uppercase tracking-[0.12em] text-[#1A2744]/70">
              Supporting Components ({supportingCount})
            </p>
          )}
          {expanded
            ? block.accessories.map((acc) => (
                <DtoolsReviewLineCardShell key={acc.id} line={acc} indent hideImage />
              ))
            : null}
        </div>
      ) : null}
    </div>
  );
}
